import InfoIcon from "@mui/icons-material/InfoOutline";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { InviteProviderFormData } from "validations/inviteProviderSchemas";
import {
  providerSpecialtyOptions,
  UserProfileFormData,
} from "validations/userSchemas";

import GenericImageUpload from "../../common/GenericImageUpload";

function PracticeInformationSection({
  profileImage,
  isInvite = false,
}: {
  profileImage: string;
  isInvite?: boolean;
}) {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<UserProfileFormData | InviteProviderFormData>();

  const { firstName, middleName, lastName } = watch();
  return (
    <Box className="space-y-6">
      <Box className="flex items-center justify-center gap-2">
        <GenericImageUpload<UserProfileFormData | InviteProviderFormData>
          name="profileImage"
          label="Upload Profile Picture"
          showButton={false}
          defaultImage={profileImage}
        />
      </Box>
      <Typography
        variant="h6"
        className="text-2xl !mt-2 font-medium text-center"
      >
        {`${firstName} ${middleName} ${lastName}`}
      </Typography>
      <Box className="flex items-center gap-2">
        <Typography variant="h6" className="text-xl font-medium">
          Practice Information{" "}
        </Typography>
        <Tooltip
          title="If you want to change your account email, please contact Cellera for further supports."
          arrow
          placement="right"
        >
          <IconButton
            sx={{
              color: "text.secondary",
              "&:hover": {
                color: "primary.main",
              },
            }}
          >
            <InfoIcon fontSize="medium" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box className="space-y-4">
        {/* Account Email */}
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Box className="relative">
              <TextField
                id="email"
                label="Account Email"
                variant="outlined"
                fullWidth
                required={isInvite}
                error={!!errors.email}
                helperText={errors.email?.message}
                slotProps={{
                  input: {
                    readOnly: !isInvite,
                  },
                }}
                {...field}
              />
            </Box>
          )}
        />

        <Box className="grid grid-cols-3 gap-4">
          {/* Provider First Name */}
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <TextField
                id="provider-first-name"
                label="First Name"
                variant="outlined"
                fullWidth
                required
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                slotProps={{
                  htmlInput: {
                    maxLength: 64,
                  },
                }}
                {...field}
              />
            )}
          />

          {/* Provider Middle Name */}
          <Controller
            name="middleName"
            control={control}
            render={({ field }) => (
              <TextField
                id="provider-middle-name"
                label="Middle Name"
                variant="outlined"
                fullWidth
                error={!!errors.middleName}
                helperText={errors.middleName?.message}
                inputProps={{ maxLength: 64 }}
                {...field}
              />
            )}
          />

          {/* Provider Last Name */}
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextField
                id="provider-last-name"
                label="Last Name"
                variant="outlined"
                fullWidth
                required
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                inputProps={{ maxLength: 64 }}
                {...field}
              />
            )}
          />
        </Box>

        {/* Provider Phone Number */}
        <Controller
          name="phoneNumber"
          control={control}
          render={({ field }) => (
            <TextField
              id="provider-phone-number"
              label="Phone Number"
              variant="outlined"
              fullWidth
              required={isInvite}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber?.message}
              slotProps={{
                htmlInput: {
                  maxLength: 15,
                  pattern: "[0-9]*",
                  inputMode: "numeric",
                },
              }}
              onKeyDown={(e) => {
                // Only allow numeric characters
                if (
                  !/[0-9]/.test(e.key) &&
                  e.key !== "Backspace" &&
                  e.key !== "Delete"
                ) {
                  e.preventDefault();
                }
              }}
              {...field}
            />
          )}
        />

        {/* NPI */}
        <Controller
          name="npi"
          control={control}
          render={({ field }) => (
            <TextField
              id="npi"
              label="NPI"
              variant="outlined"
              fullWidth
              error={!!errors.npi}
              helperText={
                errors.npi?.message ||
                "If you are a doctor/provider, you will need to enter your NPI here"
              }
              slotProps={{
                htmlInput: {
                  maxLength: 10,
                  pattern: "[0-9]*",
                  inputMode: "numeric",
                },
              }}
              onKeyDown={(e) => {
                // Only allow numeric characters
                if (
                  !/[0-9]/.test(e.key) &&
                  e.key !== "Backspace" &&
                  e.key !== "Delete"
                ) {
                  e.preventDefault();
                }
              }}
              {...field}
            />
          )}
        />

        {/* Provider Specialty */}
        <Controller
          name="specialties"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={!!errors.specialties}>
              <FormLabel
                component="legend"
                className="text-sm font-medium text-gray-700 mb-2"
              >
                Provider Specialty
              </FormLabel>
              <FormGroup>
                <Box className="grid grid-cols-5 gap-2">
                  {providerSpecialtyOptions.map((specialty) => (
                    <FormControlLabel
                      key={specialty}
                      control={
                        <Checkbox
                          checked={(field.value || []).includes(specialty)}
                          onChange={(e) => {
                            const currentValues = field.value || [];
                            if (e.target.checked) {
                              field.onChange([...currentValues, specialty]);
                            } else {
                              field.onChange(
                                currentValues.filter(
                                  (value: string | undefined) =>
                                    !!value && value !== specialty
                                )
                              );
                            }
                          }}
                        />
                      }
                      label={specialty}
                      className="text-sm"
                    />
                  ))}
                </Box>
              </FormGroup>
              {errors.specialties && (
                <FormHelperText>{errors.specialties.message}</FormHelperText>
              )}
            </FormControl>
          )}
        />
      </Box>
    </Box>
  );
}

export default PracticeInformationSection;
